import {
  Part,
  PartPayload,
  PartsSearchResponse,
  PriceImportResult,
  PurchaseOrder,
  PurchaseOrderLinePayload,
  PurchaseOrderStatus,
  StockMovement,
  SupplierStatus,
} from '../../types';
import api from './client';

/** The shop's parts and stock. Stock only moves through the ledger. */
export const inventoryService = {
  listParts: async (
    params: {
      search?: string;
      lowStock?: boolean;
      activeOnly?: boolean;
      limit?: number;
      offset?: number;
      shopId?: string;
    } = {},
  ): Promise<{ rows: Part[]; total: number }> => {
    const response = await api.get<Part[]>('/inventory/parts', { params });
    return {
      rows: response.data,
      total: Number(response.headers['x-total-count'] ?? response.data.length),
    };
  },
  createPart: async (payload: PartPayload, shopId?: string): Promise<Part> => {
    const response = await api.post<Part>('/inventory/parts', payload, { params: { shopId } });
    return response.data;
  },
  updatePart: async (id: string, payload: Partial<PartPayload>): Promise<Part> => {
    const response = await api.patch<Part>(`/inventory/parts/${id}`, payload);
    return response.data;
  },
  removePart: async (id: string): Promise<void> => {
    await api.delete(`/inventory/parts/${id}`);
  },
  adjustStock: async (id: string, payload: { quantity: number; note: string }): Promise<Part> => {
    const response = await api.post<Part>(`/inventory/parts/${id}/adjust`, payload);
    return response.data;
  },
  movements: async (id: string): Promise<StockMovement[]> => {
    const response = await api.get<StockMovement[]>(`/inventory/parts/${id}/movements`);
    return response.data;
  },
  importPriceFile: async (
    file: File,
    options: { updateStock: boolean; supplier?: string; markupPercent?: number },
    shopId?: string,
  ): Promise<PriceImportResult> => {
    const form = new FormData();
    form.append('file', file);
    form.append('updateStock', String(options.updateStock));
    if (options.supplier) form.append('supplier', options.supplier);
    if (options.markupPercent !== undefined) {
      form.append('markupPercent', String(options.markupPercent));
    }
    const response = await api.post<PriceImportResult>('/inventory/import', form, {
      params: { shopId },
    });
    return response.data;
  },
};

export const purchaseOrdersService = {
  list: async (
    params: { status?: PurchaseOrderStatus; limit?: number; offset?: number; shopId?: string } = {},
  ): Promise<{ rows: PurchaseOrder[]; total: number }> => {
    const response = await api.get<PurchaseOrder[]>('/purchase-orders', { params });
    return {
      rows: response.data,
      total: Number(response.headers['x-total-count'] ?? response.data.length),
    };
  },
  get: async (id: string): Promise<PurchaseOrder> => {
    const response = await api.get<PurchaseOrder>(`/purchase-orders/${id}`);
    return response.data;
  },
  create: async (
    payload: {
      supplierProvider: string;
      supplierName?: string;
      appointmentId?: string;
      notes?: string;
      lines: PurchaseOrderLinePayload[];
    },
    shopId?: string,
  ): Promise<PurchaseOrder> => {
    const response = await api.post<PurchaseOrder>('/purchase-orders', payload, {
      params: { shopId },
    });
    return response.data;
  },
  submit: async (id: string): Promise<PurchaseOrder> => {
    const response = await api.post<PurchaseOrder>(`/purchase-orders/${id}/submit`);
    return response.data;
  },
  /** Omit `lines` to receive everything still outstanding. */
  receive: async (
    id: string,
    lines?: { lineId: string; quantity: number }[],
  ): Promise<PurchaseOrder> => {
    const response = await api.post<PurchaseOrder>(
      `/purchase-orders/${id}/receive`,
      lines ? { lines } : {},
    );
    return response.data;
  },
  cancel: async (id: string): Promise<PurchaseOrder> => {
    const response = await api.post<PurchaseOrder>(`/purchase-orders/${id}/cancel`);
    return response.data;
  },
};

/** Parts suppliers: accounts, cross-supplier search and punchout sessions. */
export const suppliersService = {
  list: async (shopId?: string): Promise<SupplierStatus[]> => {
    const response = await api.get<SupplierStatus[]>('/suppliers', { params: { shopId } });
    return response.data;
  },
  connect: async (
    provider: string,
    credentials: Record<string, string>,
    shopId?: string,
  ): Promise<SupplierStatus[]> => {
    const response = await api.put<SupplierStatus[]>(
      `/suppliers/${provider}/connection`,
      { credentials },
      { params: { shopId } },
    );
    return response.data;
  },
  disconnect: async (provider: string, shopId?: string): Promise<void> => {
    await api.delete(`/suppliers/${provider}/connection`, { params: { shopId } });
  },
  search: async (
    query: { keyword?: string; partNumber?: string; vehicle?: { vin?: string } },
    shopId?: string,
  ): Promise<PartsSearchResponse> => {
    const response = await api.post<PartsSearchResponse>('/suppliers/search', query, {
      params: { shopId },
    });
    return response.data;
  },
  /** Opens a supplier catalogue session; returns the URL to open in a new window. */
  startPunchout: async (
    provider: string,
    payload: { appointmentId?: string; keyword?: string; vehicle?: { vin?: string } },
    shopId?: string,
  ): Promise<{ url: string; purchaseOrderId: string }> => {
    const response = await api.post<{ url: string; purchaseOrderId: string }>(
      `/suppliers/${provider}/punchout`,
      payload,
      { params: { shopId } },
    );
    return response.data;
  },
};
